"use client"
import { Text } from "@/elements"
import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image, { StaticImageData } from "next/image"
import { useRef, useState } from "react"
import { DragCursorWrapper } from "../components/useDragCursor"

import jobDashboardDark from "@/public/marketing/features/Darkmode/Job Dashboard.jpg"
import postJobsDark from "@/public/marketing/features/Darkmode/Job Post.jpg"
import listsDark from "@/public/marketing/features/Darkmode/Lists.jpg"
import messagingDark from "@/public/marketing/features/Darkmode/Messaging.jpg"
import shareJobDark from "@/public/marketing/features/Darkmode/Share Job.jpg"

import jobDashboardLight from "@/public/marketing/features/Lightmode/Job Dashboard.jpg"
import postJobsLight from "@/public/marketing/features/Lightmode/Job Post.jpg"
import listsLight from "@/public/marketing/features/Lightmode/Lists.jpg"
import messagingLight from "@/public/marketing/features/Lightmode/Messaging.jpg"
import shareJobLight from "@/public/marketing/features/Lightmode/Share Job.jpg"
import { useInView } from "motion/react"

interface Feature {
  title: string
  subtitle: string
  imageDark?: StaticImageData
  imageLight?: StaticImageData
  videoUrlDark?: string
  videoUrlLight?: string
  onClick?: () => void
}

const hiringFeatures: Feature[] = [
  {
    title: "Post jobs in seconds",
    subtitle: "Post jobs with ease and customize to get exactly what you’re looking for.",
    imageDark: postJobsDark,
    imageLight: postJobsLight,
  },
  {
    title: "Message creatives",
    subtitle: "Communicate directly with creatives who might be a fit for your project.",
    imageDark: messagingDark,
    imageLight: messagingLight,
  },
  {
    title: "Discover new talent, curated by us",
    subtitle: "Curated lists that highlight creatives in the NOVA community who are pushing our industry forward.",
    imageDark: listsDark,
    imageLight: listsLight,
  },
  {
    title: "Job tracking",
    subtitle: "Keep track of all your incoming applicants and potential hires on your Job dashboard.",
    imageDark: jobDashboardDark,
    imageLight: jobDashboardLight,
  },
  {
    title: "Share jobs directly to socials",
    subtitle: "Share your job on other social platforms to maximize visibility and engagement.",
    imageDark: shareJobDark,
    imageLight: shareJobLight,
  },
]

const findWorkFeatures: Feature[] = [
  {
    title: "Build your portfolio",
    subtitle: "Showcase your work, link representation, and more.",
    videoUrlDark: "/marketing/features/Darkmode/Feature_Portfolio-ALT.hevc_1.mp4",
    videoUrlLight: "/marketing/features/Lightmode/Portfolio-Light.hevc.mp4",
  },
  {
    title: "Expand your network",
    subtitle: "Connect with vetted, industry-leading Creatives and Clients",
    videoUrlDark: "/marketing/features/Darkmode/Connect.hevc.mp4",
    videoUrlLight: "/marketing/features/Lightmode/Connect-Light.hevc.mp4",
  },

  {
    title: "Instant job alerts",
    subtitle: "Be the first to hear about new jobs being posted that fit your criteria.",
    videoUrlDark: "/marketing/features/Darkmode/Feature_Notification.mp4",
    videoUrlLight: "/marketing/features/Lightmode/Notification-Light.hevc.mp4",
  },
  {
    title: "Tag your crew",
    subtitle: "Quickly tag and notify all the crew members who worked on your project.",
    videoUrlDark: "/marketing/features/Darkmode/Credits.hevc.mp4",
    videoUrlLight: "/marketing/features/Lightmode/Credits-copy.hevc.mp4",
  },
]

export const FeaturesSection = () => {
  const [selectedTab, setSelectedIndex] = useState<"client" | "creative">("client")

  const titleContainerRef = useRef<HTMLDivElement>(null)

  const inView = useInView(titleContainerRef as React.RefObject<Element>, { once: true })

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: (viewSize, snapSize, index) => {
      return titleContainerRef?.current?.getBoundingClientRect().left || 0
    },
    containScroll: "trimSnaps",
    skipSnaps: false,
    dragFree: false,
    slidesToScroll: 1,
    startIndex: 0,
    loop: true,
    inViewThreshold: 0,
    watchResize: true,
  })
  const [emblaRef2, emblaApi2] = useEmblaCarousel({
    align: (viewSize, snapSize, index) => {
      return titleContainerRef?.current?.getBoundingClientRect().left || 0
    },
    containScroll: "trimSnaps",
    skipSnaps: false,
    dragFree: false,
    slidesToScroll: 1,
    startIndex: 0,
    loop: true,
    inViewThreshold: 0,
    watchResize: true,
  })

  return (
    <>
      <div className="flex w-full flex-col gap-y-9 md:gap-y-20" ref={titleContainerRef} id="titlecon">
        <div className="flex flex-col justify-between gap-y-9 md:flex-row md:items-end">
          <div className="flex flex-col gap-y-4 md:gap-y-2">
            <Text variant="h6" className="text-marketing-brand-1">
              Features
            </Text>
            <Text variant="h4" className="text-marketing-primary-light dark:text-marketing-primary-dark">
              A do-it-all tool for creative work
            </Text>
          </div>
          <div className="relative flex h-[54px] w-full flex-row items-center justify-between gap-x-1 rounded-full bg-marketing-background-2-light text-marketing-primary-light md:w-[330px] dark:bg-marketing-background-2-dark dark:text-marketing-primary-dark">
            <div
              className={clsx(
                "absolute h-[44px] rounded-full bg-marketing-brand-1 transition-all",
                selectedTab === "creative"
                  ? "w-1/2 translate-x-[calc(100%-5px)] md:w-[180px] md:translate-x-[145px]"
                  : "w-1/2 translate-x-[5px] md:w-[125px] md:translate-x-[5px]"
              )}
            />
            <Text
              onClick={() => setSelectedIndex("client")}
              className={clsx(
                "z-[1] flex h-[44px] flex-1 shrink-0 cursor-pointer items-center justify-center rounded-full px-8 transition-all md:flex-auto",
                selectedTab === "client" ? "text-marketing-primary-light" : ""
              )}>
              For hiring
            </Text>
            <Text
              onClick={() => setSelectedIndex("creative")}
              className={clsx(
                "z-[1] flex h-[44px] flex-1 shrink-0 cursor-pointer items-center justify-center rounded-full px-8 transition-all md:flex-auto",
                selectedTab === "creative" ? "text-marketing-primary-light" : ""
              )}>
              For finding work
            </Text>
          </div>
        </div>
      </div>
      <div className="mt-[-180px] hidden h-[430px] w-screen overflow-x-hidden overflow-y-hidden pr-8 md:block">
        <DragCursorWrapper>
          <div
            ref={emblaRef}
            className={clsx(
              "marketing-transition-button-text",
              selectedTab === "client" ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
            )}>
            <div className="flex flex-row">
              {[...hiringFeatures, ...hiringFeatures].map((feature, index) => (
                <FeatureCard
                  onClick={() => {
                    emblaApi?.scrollTo(index)
                  }}
                  {...feature}
                  key={`client-${index}`}
                />
              ))}
            </div>
          </div>
          {inView ? (
            <div
              ref={emblaRef2}
              className={clsx(
                "marketing-transition-button-text",
                selectedTab === "creative" ? "-translate-y-full opacity-100" : "translate-y-0 opacity-0"
              )}>
              <div className="flex flex-row">
                {[...findWorkFeatures, ...findWorkFeatures].map((feature, index) => (
                  <FeatureCard
                    onClick={() => {
                      emblaApi2?.scrollTo(index)
                    }}
                    {...feature}
                    key={`creative-${index}`}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </DragCursorWrapper>
      </div>
      <div className="mt-[-124px] flex flex-col gap-y-12 md:hidden md:gap-y-9">
        {(selectedTab === "client" ? hiringFeatures : findWorkFeatures).map((feature, index) => (
          <FeatureCard {...feature} key={`${selectedTab}-${index}`} />
        ))}
      </div>
    </>
  )
}

const FeatureCard: React.FC<Feature> = ({
  title,
  subtitle,
  imageDark,
  imageLight,
  videoUrlDark,
  videoUrlLight,
  onClick,
}) => {
  return (
    <div onClick={onClick} className="relative flex w-full flex-col gap-y-4 md:mx-4 md:min-w-[408px]">
      <div className="aspect-[488/409] w-full shrink-0 overflow-hidden rounded-lg bg-marketing-background-2-light md:w-[408px] dark:bg-marketing-background-2-dark">
        {imageDark && <Image className="hidden dark:block" src={imageDark} alt={title} width={1952} height={1636} />}
        {imageLight && <Image className="block dark:hidden" src={imageLight} alt={title} width={1952} height={1636} />}
        {videoUrlDark && (
          <video
            className="hidden h-full dark:block"
            src={videoUrlDark}
            playsInline
            title={title}
            width={1952}
            height={1636}
            autoPlay
            muted
            loop
            preload="metadata">
            <source src={videoUrlDark} type="video/mp4" />
          </video>
        )}
        {videoUrlLight && (
          <video
            className="block dark:hidden"
            src={videoUrlLight}
            playsInline
            title={title}
            width={1952}
            height={1636}
            autoPlay
            muted
            loop
            preload="metadata">
            <source src={videoUrlLight} type="video/mp4" />
          </video>
        )}
      </div>
      <div className="flex w-full flex-col gap-y-1">
        <Text variant="h6" className="text-marketing-primary-light dark:text-marketing-primary-dark">
          {title}
        </Text>
        <Text className="text-marketing-secondary">{subtitle}</Text>
      </div>
    </div>
  )
}
